import React, { useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { TokenInput } from '../tiptap/token-input';
import type { ToolInputRefIssue } from '../tool-input-ref-issues';
import { RefIssueNotice } from '../ref-issue-notice';
import type { ToolInputControlProps } from './index';
import {
  parsePairs,
  PairListOps,
  serializePairs,
  type ColumnValuePair,
} from './column-value-list-model';

/**
 * Edits an array of `{ column, value }` pairs — a column name typed by hand and
 * a value that can be typed literally or pulled from an earlier step with a
 * `{{ref}}` token, the same paradigm every other input uses.
 *
 * Registered on both `set` and `where` of `bigquery-sql/update`; each instance
 * owns the one param it is bound to (`field.name`), so its cells' ref issues —
 * keyed under `<field>.<index>.value` — reach it. The value is bound as a query
 * parameter by the tool, never spliced into SQL.
 */
export const ColumnValueListControl: React.FC<ToolInputControlProps> = ({
  field,
  params,
  sources,
  refIssues,
  setValues,
}) => {
  const pairs = useMemo(
    () => parsePairs(params[field.name]),
    [params, field.name]
  );

  const commit = (next: readonly ColumnValuePair[]): void => {
    setValues({ [field.name]: serializePairs(next) });
  };

  // Cell issues are reported against `<field>.<index>.value`; map them back to
  // the row they belong to. Anything else is surfaced at the end.
  const { issuesByRow, otherIssues } = useMemo(() => {
    const re = new RegExp(`^${field.name}\\.(\\d+)\\.value$`);
    const byRow = new Map<number, ToolInputRefIssue[]>();
    const leftover: ToolInputRefIssue[] = [];
    for (const issue of refIssues) {
      const match = re.exec(issue.path);
      const index = match === null ? -1 : Number(match[1]);
      if (index < 0 || index >= pairs.length) {
        leftover.push(issue);
        continue;
      }
      const existing = byRow.get(index);
      if (existing === undefined) {
        byRow.set(index, [issue]);
      } else {
        existing.push(issue);
      }
    }
    return { issuesByRow: byRow, otherIssues: leftover };
  }, [refIssues, field.name, pairs.length]);

  return (
    <div className="flex flex-col gap-2">
      {pairs.length === 0 ? (
        <Text.Caption className="text-muted-foreground">
          Nothing set yet.
        </Text.Caption>
      ) : (
        pairs.map((pair, index) => (
          <div
            key={index}
            className="border-border/60 flex flex-col gap-1.5 rounded-md border p-2"
          >
            <div className="flex items-center gap-2">
              <Input
                className="h-8 w-full font-mono text-xs"
                placeholder="column_name"
                spellCheck={false}
                value={pair.column}
                onChange={(event) =>
                  commit(
                    PairListOps.setColumn(pairs, index, event.target.value)
                  )
                }
              />
              <button
                type="button"
                onClick={() => commit(PairListOps.removeAt(pairs, index))}
                title="Remove"
                aria-label={`Remove ${field.name} entry ${index + 1}`}
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-6 shrink-0 items-center justify-center rounded transition-colors"
              >
                <X className="size-3.5" />
              </button>
            </div>
            <TokenInput
              value={pair.value}
              placeholder="Value — type, or press / for a previous step"
              sources={sources}
              onChange={(template) =>
                commit(PairListOps.setValue(pairs, index, template))
              }
            />
            <RefIssueNotice issues={issuesByRow.get(index) ?? []} />
          </div>
        ))
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 self-start"
        onClick={() => commit(PairListOps.add(pairs))}
      >
        <Plus className="mr-1 size-3.5" />
        Add a column
      </Button>
      <RefIssueNotice issues={otherIssues} />
    </div>
  );
};
